import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RegistrationData } from '../../pages/RegisterPage';

interface ReviewStepProps {
  data: RegistrationData;
  onSubmit: () => void;
  onBack: () => void;
  loading: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  onSubmit,
  onBack,
  loading,
}) => {
  const { t } = useTranslation();
  const getRoleIcon = () => {
    return data.role === 'CLIENT' ? <BusinessIcon /> : <WorkIcon />;
  };

  const getRoleTitle = () => {
    return data.role === 'CLIENT' ? t('auth.register.roleSelection.client') : t('auth.register.roleSelection.provider');
  };

  const getRoleColor = () => {
    return data.role === 'CLIENT' ? '#28A0F0' : '#ec4899';
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          mb: 4,
          fontWeight: 600,
          color: '#ffffff',
        }}
      >
        {t('auth.register.review.title')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          mb: 4,
          color: '#a0a0a0',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {t('auth.register.review.subtitle')}
      </Typography>

      <Grid container spacing={3}>
        {/* Role Information */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '50%',
                    backgroundColor: `${getRoleColor()}20`,
                    color: getRoleColor(),
                    mr: 2,
                  }}
                >
                  {getRoleIcon()}
                </Box>
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  Роля: {getRoleTitle()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Role-specific Information */}
        {data.role === 'CLIENT' && (
          <Grid item xs={12}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Информация за компанията
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Компания
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                      {data.companyName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Размер
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                      {data.companyName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Отрасъл
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                      IT & AI
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {data.role === 'FREELANCER' && (
          <Grid item xs={12}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  Професионална информация
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 1 }}>
                      Умения
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {data.skills?.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          size="small"
                          sx={{
                            backgroundColor: '#28A0F0',
                            color: '#ffffff',
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Опит
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                      {data.bio || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Почасова ставка
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#ffffff' }}>
                      {data.hourlyRate} лв.
                    </Typography>
                  </Grid>
                  {data.portfolioUrl && (
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                        Портфолио
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#28A0F0' }}>
                        {data.portfolioUrl}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Contact Information */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Контактни данни
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ color: '#28A0F0', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Име
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#ffffff' }}>
                    {data.firstName} {data.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ color: '#28A0F0', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Имейл
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#ffffff' }}>
                    {data.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ color: '#28A0F0', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      Телефон
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#ffffff' }}>
                    {data.phone}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Terms Acceptance */}
        <Grid item xs={12}>
          <Alert
            severity="info"
            sx={{
              backgroundColor: 'rgba(40, 160, 240, 0.1)',
              border: '1px solid rgba(40, 160, 240, 0.3)',
              color: '#ffffff',
            }}
          >
            <Typography variant="body2">
              Съгласихте се с Условията за ползване и Политиката за поверителност.
              Заявката ви ще бъде прегледана от администратор преди одобрение.
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={loading}
          sx={{
            borderColor: '#2d2d44',
            color: '#ffffff',
            px: 4,
            py: 1.5,
            '&:hover': {
              borderColor: '#28A0F0',
              backgroundColor: 'rgba(40, 160, 240, 0.1)',
            },
            '&:disabled': {
              borderColor: '#2d2d44',
              color: '#6b7280',
            },
          }}
        >
          {t('auth.register.review.back')}
        </Button>

        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            px: 6,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
            },
            '&:disabled': {
              background: '#2d2d44',
              color: '#6b7280',
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              {t('auth.register.review.submitting')}
            </Box>
          ) : (
            t('auth.register.review.submit')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewStep;
